import fscreen from "fscreen";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { IGif } from "services/types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TABLET_WIDTH } from "hooks/useDeviceType";
import { metrics } from "utils/metrics";

interface IUseFullScreen {
  ref: any;
}

export const useFullScreen = ({ ref }: IUseFullScreen) => {
  const location = useLocation();
  const [inFullscreenMode, setInFullscreenMode] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [FSGif, setFSGif] = useState<IGif | null>(null);
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);
  const WrapEL = document.querySelector(".Wrapper");
  const offsetTop = isDesktop ? window.scrollY : (WrapEL?.scrollTop || 0);

  const scrollAction = useCallback(() => {
    if (isDesktop) {
      window.scrollTo(0, scrollPosition);
    } else {
      WrapEL?.scrollTo(0, scrollPosition);
    }
  }, [scrollPosition, WrapEL, isDesktop]);

  useEffect(() => {
    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener("fullscreenchange",handleFullscreenChange, false);
      fscreen.addEventListener("fullscreenerror", handleFullscreenError, false);

      return () => {
        fscreen.removeEventListener("fullscreenchange", handleFullscreenChange);
        fscreen.removeEventListener("fullscreenerror", handleFullscreenError);
      };
    }
  });

  useEffect(() => {
    if (inFullscreenMode) {
      document.body.classList.add("fullScreen");
      metrics.sendEnterFullscreen();
    } else {
      document.body.classList.remove("fullScreen");
      scrollAction();
    }
  }, [inFullscreenMode, scrollAction]);

  useEffect(() => {
    return () => {
      if (document.fullscreenElement && document.hasFocus()) {
        fscreen.exitFullscreen();
      }

      document.body.classList.remove("fullScreen");
    };
  }, [location]);

  const handleFullscreenChange = useCallback((e: any) => {
    if (fscreen.fullscreenElement !== null) {
      console.debug("Entered fullscreen mode", e);
      setInFullscreenMode(true);
    } else {
      console.debug("Exited fullscreen mode", e);
      setInFullscreenMode(false);
    }
  }, []);

  const handleFullscreenError = useCallback((e: any) => {
    console.debug("Fullscreen Error", e);
  }, []);

  const toggleFullscreen = useCallback((gif: IGif | null) => {
    const fsEnabled = fscreen.fullscreenEnabled;

    if (inFullscreenMode && !fsEnabled) {
      setFSGif(null);
      setInFullscreenMode(false);
    }

    if (!inFullscreenMode && !fsEnabled){
      setFSGif(gif);
      setScrollPosition(offsetTop);
      setInFullscreenMode(true);
    }

    if (fsEnabled && inFullscreenMode) {
      setFSGif(null);
      if (document.hasFocus()) {
        fscreen.exitFullscreen();
      }
    }

    if (fsEnabled && !inFullscreenMode) {
      setFSGif(gif);
      setScrollPosition(offsetTop);
      fscreen.requestFullscreen(ref.current);
    }
  }, [inFullscreenMode, ref, offsetTop]);

  return {
    inFullscreenMode,
    toggleFullscreen,
    FSGif,
    fullscreenEnabled: fscreen.fullscreenEnabled
  };
};
